/**
 * Auth service — JWT-based authentication against the custom Node + Express backend.
 * Tokens are stored in localStorage (refresh) and in-memory via AuthContext (access).
 *
 * Endpoints used:
 *   POST /auth/register
 *   POST /auth/login
 *   POST /auth/refresh
 *   GET  /auth/me
 *   POST /auth/change-password
 */

import {
  apiPost,
  apiGetAuth,
  apiPostAuth,
  apiPatchAuth,
  isApiConfigured,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from "./http";

// ── Token storage ─────────────────────────────────────────────────────────────
// Keys live in the HTTP layer so its automatic 401-refresh can read/write them.

export function storeTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY,  access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserRole = "admin" | "owner" | "user";

export interface AuthUser {
  id:            number;
  uuid:          string;
  role:          UserRole;
  name:          string;
  email:         string;
  phone:         string | null;
  emailVerified: boolean;
  avatarUrl:     string | null;
}

export interface AuthTokens {
  accessToken:  string;
  refreshToken: string;
  user:         AuthUser;
}

export interface RegisterData {
  name:     string;
  email:    string;
  phone?:   string;
  password: string;
}

// ── Demo user for offline mode ────────────────────────────────────────────────

export const DEMO_USER: AuthUser = {
  id:            1,
  uuid:          "demo-user-0000",
  role:          "owner",
  name:          "Demo Business Owner",
  email:         "owner@coastalbizzconnect.com",
  phone:         "+91 98440 00000",
  emailVerified: true,
  avatarUrl:     null,
};

// ── API calls ─────────────────────────────────────────────────────────────────

export async function registerUser(data: RegisterData): Promise<AuthTokens> {
  const tokens = await apiPost<AuthTokens>("/auth/register", data);
  storeTokens(tokens.accessToken, tokens.refreshToken);
  return tokens;
}

export async function loginUser(email: string, password: string): Promise<AuthTokens> {
  const tokens = await apiPost<AuthTokens>("/auth/login", { email, password });
  storeTokens(tokens.accessToken, tokens.refreshToken);
  return tokens;
}

export async function refreshAccessToken(): Promise<AuthTokens | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;
  try {
    const tokens = await apiPost<AuthTokens>("/auth/refresh", { refreshToken });
    storeTokens(tokens.accessToken, tokens.refreshToken);
    return tokens;
  } catch {
    clearTokens();
    return null;
  }
}

export async function getCurrentUser(accessToken: string): Promise<AuthUser> {
  return apiGetAuth<AuthUser>("/auth/me", accessToken);
}

export async function changeUserPassword(
  token: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiPostAuth<null>("/auth/change-password", { currentPassword, newPassword }, token);
}

/** Server-side logout — revokes the refresh token, then clears local storage. */
export async function logoutUser(): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  if (isApiConfigured() && refreshToken) {
    try {
      await apiPost<null>("/auth/logout", { refreshToken });
    } catch {
      /* best-effort — always clear locally */
    }
  }
  clearTokens();
}

/** Read the signed-in user's profile from the backend (live DB row). */
export async function getProfile(accessToken: string): Promise<AuthUser> {
  return apiGetAuth<AuthUser>("/auth/profile", accessToken);
}

export interface ProfileUpdate {
  name?:      string;
  phone?:     string;
  avatarUrl?: string;
}

/** Update the signed-in user's profile. */
export async function updateProfile(
  accessToken: string,
  data: ProfileUpdate,
): Promise<AuthUser> {
  return apiPatchAuth<AuthUser>("/auth/profile", data, accessToken);
}

/** Try to restore session from stored tokens on app mount */
export async function restoreSession(): Promise<{ user: AuthUser; accessToken: string } | null> {
  if (!isApiConfigured()) return null;

  // Try access token first
  const accessToken = getStoredAccessToken();
  if (accessToken) {
    try {
      const user = await getCurrentUser(accessToken);
      return { user, accessToken };
    } catch {
      /* token may have expired — try refresh */
    }
  }

  // Try refresh token
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    return { user: refreshed.user, accessToken: refreshed.accessToken };
  }

  clearTokens();
  return null;
}